import { Fragment, useState, useRef, useContext } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import { headerDropLeadFromSchema } from "@/helpers/schema_validation";
import { hash } from "@/helpers/utility/crypto";
import useGtm, { GTM_EVENT } from "@/hooks/useGtm";
import Image from "next/image";
import Button from "@/components/UI/button/button";
import CustomerContext from "@/stores/customer-context";
import style from "./droplead_modal.module.css";
import { AlertContext } from "@/stores/alert-context";
import { AlertAction } from "@/helpers/utility/alerts/alert_actions";
import { ActionType } from "@/helpers/constant/enum_data";
import { ConsentPrivacyPolicy } from "@/components/common/consent-privacy-policy/consent_privacy_policy";

function HomeTypeItem({
  onHomeTypeClick: onParentHomeTypeClickHandler,
  setFieldValue,
}) {
  const [selectedHometype, setSelectedHometype] = useState();

  const homeType = [
    {
      name: "บ้านเดี่ยว",
      name_en: "house",
      image: "home-type-detached-outline-icon.svg",
    },
    {
      name: "บ้านแฝด",
      name_en: "twinHouse",
      image: "home-type-semi-detached-outline-icon.svg",
    },
    {
      name: "คอนโด",
      name_en: "condo",
      image: "home-type-condominium-outline-icon.svg",
    },
    {
      name: "ทาวน์เฮ้าส์",
      name_en: "townHouse",
      image: "home-type-townhouse-outline-icon.svg",
    },
    {
      name: "ตึกแถว",
      name_en: "shopHouse",
      image: "home-type-shophouse-outline-icon.svg",
    },
  ];

  const homeTypeClickHandler = (setFieldValue, homeTypeName) => {
    setSelectedHometype(homeTypeName);
    onParentHomeTypeClickHandler(setFieldValue, homeTypeName);
  };

  return (
    <Fragment>
      {homeType.map((type, index) => (
        <div
          key={index}
          onClick={() => homeTypeClickHandler(setFieldValue, type.name_en)}
          className={`${style.home_type_item} ${
            selectedHometype === type.name_en ? style.selected : ""
          }`}
        >
          <figure className={style.home_type_logo_wrapper}>
            <Image
              src={"/assets/icon-svg/" + type.image}
              alt={type.name}
              width="100%"
              height="100%"
              objectFit="contain"
            />
          </figure>
          <p>{type.name}</p>
        </div>
      ))}
    </Fragment>
  );
}

export default function DropLeadModal({ isOpen = false, setIsOpen }) {
  const [state, dispatch] = useContext(AlertContext);
  const customerCtx = useContext(CustomerContext);
  const modalRef = useRef(null);
  const { onSendGtmEvent } = useGtm();
  const [selectedHometype, setSelectedHometype] = useState();
  const [clickSubmit, setClickSubmit] = useState(false);
  const handleOnCloseModal = () => {
    setIsOpen(false);
  };

  const onSubmitHandler = async (customerDropleadInfo) => {
    const { homeType, firstName, lastName, phone } = customerDropleadInfo;
    if (!homeType && !firstName && !lastName && !phone) return;

    try {
      const { homeType, ...customerInfo } = customerDropleadInfo;
      const homeInfo = { homeType };
      await customerCtx.setHomeInfo(homeInfo);
      await customerCtx.setCustomerInfo(customerInfo, "dropleadHeader");
      const hashedPhone = await hash(customerInfo.phone);
      const _userId = await customerCtx.getUserId();
      const gtmEvent = {
        transaction_id: _userId,
        phone: hashedPhone,
      };
      onSendGtmEvent(GTM_EVENT.DROP_LEAD, gtmEvent, "header");
      dispatch(
        AlertAction.showSuccessAlert(
          "ท่านได้ส่งข้อมูลเพื่อให้พนักงานติดต่อกลับเรียบร้อยแล้ว บริษัทจะติดต่อกลับท่านเพื่อให้บริการ"
        )
      );
      setIsOpen(false);
    } catch (err) {
      setIsOpen(true);
      console.warn(err.message);
      dispatch(
        AlertAction.showErrorAlert(
          "ขออภัย! เกิดข้อผิดพลาดขึ้น",
          ActionType.CONFIRM
        )
      );
    }
  };

  const onHomeTypeClickHandler = (setFieldValue, homeTypeName) => {
    setSelectedHometype(homeTypeName);
    setFieldValue("homeType", homeTypeName);
  };

  return (
    <Fragment>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          initialFocus={modalRef}
          className={style.modal}
          onClose={() => {}}
        >
          {/* <div className="min-h-screen px-4 text-center"> */}
          <div>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className={style.overlay} />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-top"
              aria-hidden="true"
            ></span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className={style.modal_container}>
                <div
                  className={style.close_modal_btn}
                  onClick={handleOnCloseModal}
                >
                  <img
                    alt="close-btn"
                    src="/assets/icon-svg/tqm-blue-remove.svg"
                  />
                </div>
                <Dialog.Title as="h3" className={style.title}>
                  <span ref={modalRef}>ให้ข้อมูลเพื่อเลือกแผนที่ตรงใจ</span>
                </Dialog.Title>
                <div className="mt-4">
                  <Formik
                    validationSchema={headerDropLeadFromSchema}
                    initialValues={{
                      homeType: "",
                      firstName: "",
                      lastName: "",
                      phone: "",
                    }}
                    onSubmit={() => setClickSubmit(true)}
                  >
                    {({
                      handleChange,
                      handleBlur,
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      isValid,
                    }) => (
                      <Form>
                        <div className={style.form_group}>
                          <p className={style.form_group_label}>
                            ข้อมูลส่วนตัวของผู้เอาประกัน
                          </p>
                          <div className={style.form_row}>
                            <div className={style.input_group}>
                              <label htmlFor="homeType">
                                เลือกประเภทที่อยู่อาศัย
                              </label>
                              <Field
                                type="hidden"
                                name="homeType"
                                title="ประเภทบ้าน"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                values={values}
                              />
                              <div className={style.home_type_items}>
                                <HomeTypeItem
                                  onHomeTypeClick={onHomeTypeClickHandler}
                                  setFieldValue={setFieldValue}
                                />
                              </div>
                              {errors.homeType && touched.homeType && (
                                <div className={style.input_error}>
                                  {errors.homeType}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={style.form_group}>
                          <p className={style.form_group_label}>
                            ข้อมูลส่วนตัวของผู้เอาประกัน
                          </p>
                          <div className={style.form_row}>
                            <div
                              className={`${style.input_group} ${
                                errors.firstName && touched.firstName
                                  ? style.error
                                  : ""
                              }`}
                            >
                              <label htmlFor="firstName">ชื่อ</label>
                              <Field
                                type="text"
                                name="firstName"
                                title="ชื่อ"
                                placeholder="ชื่อ"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                values={values}
                                errors={errors}
                                touched={touched}
                                className={style.field}
                              />
                              {errors.firstName && touched.firstName && (
                                <div className={style.input_error}>
                                  {errors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`${style.input_group} ${
                                errors.lastName && touched.lastName
                                  ? style.error
                                  : ""
                              }`}
                            >
                              <label htmlFor="lastName">นามสกุล</label>
                              <Field
                                type="text"
                                name="lastName"
                                title="นามสกุล"
                                placeholder="นามสกุล"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                values={values}
                                errors={errors}
                                touched={touched}
                                className={style.field}
                              />
                              {errors.lastName && touched.lastName && (
                                <div className={style.input_error}>
                                  {errors.lastName}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={style.form_row}>
                            <div
                              className={`${style.input_group} ${
                                errors.phone && touched.phone ? style.error : ""
                              }`}
                            >
                              <label htmlFor="phone">เบอร์โทรศัพท์มือถือ</label>
                              <Field
                                type="tel"
                                name="phone"
                                title="เบอร์โทรศัพท์มือถือ"
                                placeholder="เบอร์โทรศัพท์มือถือ"
                                maxlength="10"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                values={values}
                                errors={errors}
                                touched={touched}
                                className={style.field}
                              />
                              {errors.phone && touched.phone && (
                                <div className={style.input_error}>
                                  {errors.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={style.button_wrapper}>
                          <ConsentPrivacyPolicy
                            clickSubmit={clickSubmit}
                            setClickSubmit={setClickSubmit}
                            onSubmit={() => onSubmitHandler(values)}
                          />
                          <Button
                            color="blue"
                            text="ตกลง"
                            enable={isValid}
                            onClick={() => setClickSubmit(true)}
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
}
